import {ConstantsService} from '../../services/constants-service';
import {ObservableRequestService} from '../../services/observable-request.service';
import {SurveyConfiguration} from '../../shared/models/survey/survey-config.model';
import {SurveyQuestion} from "../../shared/models/survey/survey-question.model";
import {SurveyTemplate} from '../../shared/models/survey/survey-template.model';
import {Injectable} from '@angular/core';
import {plainToClass} from 'class-transformer';
import {Observable} from 'rxjs/Observable';
import {SurveyResponse} from '../../shared/models/survey/survey-response.model';

/**
 * Service to manage all the survey related rest integrations.
 *
 * @author pankaj.khurana@vernuso.com (Pankaj Khurana)
 */
@Injectable()
export class SurveyManagementService {
  private surveyTemplates: SurveyTemplate[];

  constructor(private requestService: ObservableRequestService) {
  }

  getSurveyConfigs(): Observable<any> {
    return this.requestService.getRequest(ConstantsService.SURVEY_CONFIG)
      .map(response => {
        return plainToClass(SurveyConfiguration, response.arrResult as Object[]);
      });
  };

  getEntityTypes(): Observable<any> {
    return this.requestService.getRequest(ConstantsService.LOAD_ENTITY_TYPES);
  }

  getSurveyTemplates(): Observable<any> {
    return this.requestService.getRequest(ConstantsService.SURVEY_TEMPLATE)
      .map(response => {
        return plainToClass(SurveyTemplate, response.arrResult as Object[]);
      });
  }

  getSurveyTemplatesFromCache(): Array<SurveyTemplate> {
    return this.surveyTemplates;
  }

  setSurveyTemplatesInCache(Templates: SurveyTemplate[]): void {
    this.surveyTemplates = Templates;
  }

  addSurveyConfig(surveyConfig: Object): Observable<any> {
    return this.requestService.postRequest(ConstantsService.SURVEY_CONFIG,
      JSON.stringify(surveyConfig),
      this.requestService.getJSONContentType());
  }

  deleteSurveyConfig(surveyId: SurveyConfiguration): Observable<any> {
    return this.requestService.deleteRequest(
      ConstantsService.SURVEY_CONFIG,
      this.requestService.serialize(surveyId),
      this.requestService.getFormEncodedContentType());
  }

  deleteSurveyTemplate(templateId: SurveyTemplate): Observable<any> {
    return this.requestService.deleteRequest(
      ConstantsService.SURVEY_TEMPLATE,
      this.requestService.serialize(templateId),
      this.requestService.getFormEncodedContentType());
  }

  addSurveyTemplate(surveyTemplate: Object): Observable<any> {
    return this.requestService.postRequest(ConstantsService.SURVEY_TEMPLATE,
      JSON.stringify(surveyTemplate),
      this.requestService.getJSONContentType());
  }

  listSurveyQuestions(templateId: number): Observable<any> {
    const surveyTemplateDetails = ConstantsService.SURVEY_TEMPLATE + '/' + templateId;
    return this.requestService.getRequest(surveyTemplateDetails)
      .map(response => {
        return plainToClass(SurveyTemplate, response.arrResult as Object[]);
      });
  }

  addSurveyQuestion(surveyQuestion: SurveyQuestion, surveyId: number, updateInstances: boolean): Observable<any> {
    return this.requestService.postRequest(ConstantsService.SURVEY_QUESTION_TEMPLATE + '/' + surveyId,
      this.requestService.getFormUrlEncoded({
        questionVO: JSON.stringify(surveyQuestion),
        updateInstances: updateInstances
      }),
      this.requestService.getFormEncodedContentType());
  }

  updateSurveyQuestion(surveyQuestion: SurveyQuestion, surveyId: number): Observable<any> {
    return this.requestService.putRequest(ConstantsService.SURVEY_QUESTION_TEMPLATE + '/' + surveyQuestion.id,
      JSON.stringify(surveyQuestion),
      this.requestService.getJSONContentType());
  }

  deleteSurveyQuestion(question: SurveyQuestion): Observable<any> {
    return this.requestService.deleteRequest(ConstantsService.SURVEY_QUESTION_TEMPLATE + '/' + question.id,
      null, this.requestService.getJSONContentType());
  }

  listSurveyResponsesForQuestion(questionTemplateId: number) {
    return this.requestService.getRequest(
      ConstantsService.SURVEY_QUESTION_INSTANCE_ANSWERED.replace(/\{questionTemplateId\}/, questionTemplateId.toString()))
      .map(response => {
        return plainToClass(SurveyResponse, response.arrResult as Object[]);
      });
  }

  listSurveyResponsesForMcqQuestionsAnsweredWithOption(questionTemplateId: number, optionTemplateIds: number[]) {
    return this.requestService.getRequest(
      ConstantsService.SURVEY_QUESTION_INSTANCE_ANSWERED_WITH_OPTION
        .replace(/\{questionTemplateId\}/, questionTemplateId.toString())
        .replace(/\{opTemplateId\}/, optionTemplateIds.join(",")))
      .map(response => {
        return plainToClass(SurveyResponse, response.arrResult as Object[]);
      });
  }

  clearSurveyResponse(surveyInstanceIds: number[]) {
    return this.requestService.putRequest(
      ConstantsService.SURVEY_QUESTION_INSTANCE_CLEAR_RESPONSE.replace(/\{questionInstanceIds\}/, surveyInstanceIds.join(",")),
      null, this.requestService.getJSONContentType());
  }

  moveTextResponse(sourceSurveyQuestion: number, targetSurveyQuestion: number) {
    return this.requestService.putRequest(ConstantsService.SURVEY_QUESTION_INSTANCE_MOVE_RESPONSE
        .replace(/\{sourceQuestionTemplateId\}/, sourceSurveyQuestion.toString())
        .replace(/\{targetQuestionTemplateId\}/, targetSurveyQuestion.toString()),
      null, this.requestService.getJSONContentType())
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/survey/survey-management.service.ts